import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Popper from "@mui/material/Popper";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import GradingIcon from "@mui/icons-material/Grading";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import LoadingButton from "_shared/_views/loading/loading-button";
import { styled } from "@mui/material/styles";

const SplitButtonContainer = styled(Box)(() => ({
  position: 'relative',
  width: '100%'
}));

type Props = {
  className?: string;
  hasPendingChanges: boolean;
  hasPendingComments: boolean;
  disabled?: boolean;
  finalized?: boolean;
  loading?: boolean;
  onFinalize: () => void;
  onSubmitChangesAndComments: () => void;
  onSubmitCommentsOnly: () => void;
};

export default function SubmitReviewButton({
  className,
  hasPendingChanges,
  hasPendingComments,
  disabled,
  finalized,
  loading,
  onFinalize,
  onSubmitChangesAndComments,
  onSubmitCommentsOnly,
}: Readonly<Props>) {
  const anchorRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const confirm = useConfirmDialog();

  React.useEffect(() => {
    setAnchorEl(anchorRef.current);
  }, []);

  const handleFinalize = async () => {
    let title = '';
    let message = '';
    if (hasPendingChanges) {
      title = "Discard Changes?";
      message = "Are you sure you want to discard pending changes and finish reviewing?";
    } else if (hasPendingComments) {
      title = 'Discard Comments?';
      message = 'Are you sure you want to discard pending comments and finish reviewing?';
    } else {
      title = "Endorse / Finalize Proposal?";
      message = "Are you sure you want to finish reviewing this proposal?";
    }

    const response = await confirm({
      title,
      message,
      okButtonText: "Yes",
    });

    if (response === ConfirmResponse.Cancel) return;

    onFinalize();
  };

  const options = [
    {
      label: finalized ? "Review Finalized" : "Endorse / Finalize Review",
      action: handleFinalize,
      disabled: finalized,
    },
    {
      label: "Submit Comments",
      action: async () => onSubmitCommentsOnly(),
      disabled: !hasPendingComments,
    },
    {
      label: "Submit Changes & Comments",
      action: async () => onSubmitChangesAndComments(),
      disabled: !hasPendingChanges,
    },
  ];

  const isDisabled = Boolean(disabled || (!hasPendingComments && !hasPendingChanges && finalized));
  const enabledOptions = options.filter(o => !o.disabled).length;

  const handlePrimaryClick = async () => {
    if (!hasPendingChanges || hasPendingComments) {
      await handleFinalize();
      return;
    }
    setOpen(true);
  };

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleMenuItemClick = async (index: number) => {
    await (options[index]).action();
    setOpen(false);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) return;
    setOpen(false);
  };

  const getButtonLabel = () => {
    if (!hasPendingComments && !hasPendingChanges && finalized) {
      return "Review Finalized";
    }
    if (hasPendingComments && !hasPendingChanges && finalized) {
      return "Submit Comments";
    }
    if (!hasPendingComments && hasPendingChanges && finalized) {
      return "Submit Changes";
    }
    if (hasPendingComments && hasPendingChanges && finalized) {
      return "Submit Changes & Comments";
    }
    return "Submit Review";
  };

  return (
    <SplitButtonContainer className={className}>
      <ButtonGroup
        variant="contained"
        ref={anchorRef}
        aria-label="submit review split button"
        sx={{ width: '100%' }}
      >
        <LoadingButton
          onClick={handlePrimaryClick}
          startIcon={<GradingIcon />}
          sx={{ width: '100%' }}
          loading={!!loading}
          disabled={isDisabled || (enabledOptions === 1 && finalized)}
        >
          {getButtonLabel()}
        </LoadingButton>
        {enabledOptions > 1 && (
          <Button
            size="small"
            aria-controls={open ? "submit-review-menu" : undefined}
            aria-expanded={open ? "true" : undefined}
            aria-haspopup="menu"
            disabled={isDisabled}
            onClick={handleToggle}
          >
            <ArrowDropDownIcon />
          </Button>
        )}
      </ButtonGroup>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        transition
        disablePortal
        sx={{ width: '100%', zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'top' }}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="submit-review-menu">
                  {options.map((option, index) => (
                    <MenuItem
                      key={option.label}
                      disableGutters
                      disabled={option.disabled}
                      sx={{ justifyContent: 'center', paddingRight: 6 }}
                      onClick={() => handleMenuItemClick(index)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </SplitButtonContainer>
  );
}
